import { QueryClient } from '@tanstack/react-query';

import { useEffect, useState, useMemo, useCallback } from 'react';

import { useRouter } from 'next/router';

import _ from 'lodash';

import PropTypes from 'prop-types';

import { CircularProgress } from '@mui/material';

import SearchBar from '../components/search-bar/search-bar.component';

import MobileSearchBar from '../components/mobile-search-bar/mobile-search-bar.component';

import DesktopFilters from '../components/desktop-filter-components/desktop-filters.component';

import RoomTypesSort from '../components/room-types/room-types-sort.component';

import RoomTypes from '../components/room-types/room-types.component';

import { Layout, Footer, MetaData } from '../components/shared';

import { cmsService} from '../services/homii-services';

import cityDomain, { useCityByName } from '../domain/city/city.domain'

import { buildingDomain, roomTypesDomain, portfolioDomain } from '../domain';

import useResponsive from '../../utils/responsive';

import ErrorResults from '../components/room-types/error-results.component';

const Bookings = (props) => {
    
    const { baseUrlApp, data } = props;

    const { isMobile, isTablet, isSmallTablet } = useResponsive();

    const [buildings, setBuildings] = useState([]);

    const [roomTypings, setRoomTypes] = useState([]);

    const [totalAvailable, setTotalAvailable] = useState(0);

    const [showNewFirst, setShowNewFirst] = useState(true);   
    
     const [sortBy, setSortBy] = useState('Sort By');

    const allRoomTypes = useMemo(() => _.map(roomTypings, (roomType) => roomType), [roomTypings]);    

    const [sortedRoomTypes, setSortedRoomTypes] = useState(allRoomTypes);

    const [allPortfolios, setAllPortfolios] = useState([]); 

    const [searchError, setSearchError] = useState(null);

    const router = useRouter();

    const { page, city } = router.query;

    const { data: selectedCity } = useCityByName(city);    

    const seoTitle = _.get(data, 'acf.seo_title');

    const seoMetaDesc = _.get(data, 'acf.seo_meta_description');

    useEffect(() => {
        if (!selectedCity) return;
        const asyncSearch = async () => {
            try {
                const fetchedBuildings = await buildingDomain.fetchBuildingsByCityId(_.get(selectedCity, 'id'));
                setBuildings(fetchedBuildings);
            } 
            catch (error) {
                setSearchError(error);
            }
            try {
                const rooms = await roomTypesDomain.fetchAvailabilityForCity(_.get(selectedCity, 'id'));
                setRoomTypes(rooms);
            } catch (error) {
                setSearchError(error);
            }
            const portfolios = await portfolioDomain.fetchAllPortfolios();
            setAllPortfolios(portfolios);
        };
        asyncSearch();
    }, [selectedCity]);       

    useEffect(() => {
        let sorted = null;
        switch (sortBy) {
            case 'Sort By':
                sorted = allRoomTypes;
                break;
            case 'Price - Low to High':
                sorted = _.sortBy(allRoomTypes, [(roomType) => roomType.startingPrice]);
                break;
            case 'Price - High to Low':
                sorted = _.sortBy(allRoomTypes, [(roomType) => roomType.startingPrice]).reverse();
                break;
            default:
                sorted = allRoomTypes;
        }
       setSortedRoomTypes(sorted);
       setShowNewFirst(sortBy === 'Sort By');
    }, [sortBy, allRoomTypes]);    

    const onAvailabilityChange = (count) => {
        setTotalAvailable(count);               
    };     

    const handleRoomSortChange = useCallback((selectedValue) => {
        setSortBy(selectedValue);
    }, []);    

    const renderDesktopSearchbar = () => {        
        if (isMobile || (isTablet && isSmallTablet)) {
            return null;
        }
        return <SearchBar type="Bookings" query={router.query} key={city} />;
    };

    const renderMobileAndTabletSearchbar = () => {
        if (!isMobile && !(isTablet && isSmallTablet)) {
            return null;
        }
        return <MobileSearchBar type="Bookings" roomTypes={sortedRoomTypes} buildings={buildings} />;
    };

    const renderAvailabilityCount = () => {       
        const headerText = _.get(selectedCity, 'name') && city !== 'Nearby'
            ? ['Explore rentals in ', <strong key={1}>{_.get(selectedCity, 'name')}</strong>]
            : ['Explore rentals ', <strong key={1}>Nearby</strong>];        
        return (
            <>
                <p style={{ marginTop: 50 }}>
                    <strong>{totalAvailable}+</strong> spaces available
                </p>
                <h3 className='explore-apartment-types-header-3' id="bookings_header">{headerText}</h3>            
            </>
        );
    };   

    const renderButtonContainer = () => {
        return (
            <div className='explore-apartment-button-container'>
                <DesktopFilters buildings={buildings} roomTypes={sortedRoomTypes} />
                <RoomTypesSort onRoomSort={handleRoomSortChange} />
            </div>
        );
    };    

    const renderRoomTypes = () => {
        return (
            <RoomTypes
                roomTypes={sortedRoomTypes}
                showNewFirst={showNewFirst}
                pageNumber={page}
                onAvailabilityChange={onAvailabilityChange}
                allPortfolios={allPortfolios}
            />
        );
    };     

    const renderSearching = () => {
        return (
            <>
                <div className='booking-searching-container search-container-row'>
                    <CircularProgress />
                </div>
            </>            
        );
    };     

    const renderSearchError = () => {
        return (
            <>                    
                <ErrorResults errorMessage={searchError.message} />                
            </>            
        );
    }; 

    const renderResults = () => {

        if(!searchError && (_.isEmpty(sortedRoomTypes) || _.isEmpty(allPortfolios))){
            return renderSearching();
        }

        if(searchError){
            return renderSearchError();
        }

        return (
            <>
                {renderAvailabilityCount()}

                {renderDesktopSearchbar()}

                {renderMobileAndTabletSearchbar()}

                {renderButtonContainer()}

                {renderRoomTypes()}           
            </>
        );
    };

    return (
        <Layout>

            <MetaData title={seoTitle} desc={seoMetaDesc} canonical={`${baseUrlApp}/bookings`} />

            <div className='container-root container-root-maxWidthLg'>

                {renderResults()}

            </div>

            <Footer />

        </Layout>
    );
};

export const getServerSideProps = async () => {
    const queryClient = new QueryClient();
    await queryClient.prefetchQuery({
        queryKey: ['cities'],
        queryFn: cityDomain.fetchAllCitiesData,
    });
    await queryClient.prefetchQuery({
        queryKey: ['selectedCity'],
        queryFn: cityDomain.fetchClosestCity,
    });     
    const data = await cmsService.RetrieveWordPressPage('bookings');
    return {
        props: {
            data
        }
    };
};

export default Bookings;

Bookings.propTypes = {
    data: PropTypes.object,
    baseUrlApp: PropTypes.string,
};
